import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { IDropdown, IItinerary } from "../../models/BaseModel";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { JVInput } from "../../shared/JVInput";
import { JVCheckbox } from "../../shared/JVCheckbox";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVTextArea } from "../../shared/JVTextarea";

const schema = yup.object({
    activityId: yup.number().required("Activity is required").min(1, "Activity is required"),
    day: yup.number().required("Day is required"),
    hour: yup.number().required("Hour is required"),
    minute: yup.number().required("Minute is required"),
    duration: yup.number().required("Duration is required").min(1, "Duration is required")
});

interface Iprops {
    singleData: IItinerary,
    destination: string,
    packageTitle: string,
    packageDuration: number,
    cityId: number,
    open: boolean,
    hideDialog: () => void,
    toast: any,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

interface IActivityDrop extends IDropdown {
    desc?: string;
}

export const EditForm = (props: Iprops) => {
    const [submitting, setSubmitting] = useState(false);
    const [activities, setActivities] = useState<IActivityDrop[]>();

    const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<IItinerary>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData,
        shouldFocusError: false,
    });

    const activityId = watch('activityId');
    const day = watch("day");
    const hour = watch("hour");
    const minute = watch("minute");
    const duration = watch("duration");
    const [endTime, setEndTime] = useState<string>();

    useEffect(() => {
        if (day !== undefined && hour !== undefined && minute !== undefined && duration !== undefined) {
            const startTime = ((day - 1) * 24 * 60) + (hour * 60) + minute;
            const endTotal = startTime + +duration;
            const end_day = Math.floor(endTotal / (24 * 60)) + 1;
            const end_hour = Math.floor((endTotal % (24 * 60)) / 60);
            const end_minute = endTotal % 60;
            setEndTime(`Day ${end_day} | ${end_hour.toString().padStart(2, "0")}:${end_minute.toString().padStart(2, "0")}`);
        } else setEndTime(undefined);
    }, [day, hour, minute, duration]);

    useEffect(() => {
        if (activityId && activities) {
            if (activityId === props.singleData.activityId) {
                setValue('duration', props.singleData.duration);
                setValue('desc', props.singleData.desc);
            } else {
                const a = activities.find(f => f.id === activityId);
                setValue('desc', a?.desc);
            }
        }
    }, [activityId]);

    useEffect(() => {
        if (props.singleData.id) {
            reset({ ...props.singleData });
        } else {
            reset(props.singleData);
        }
    }, [props.singleData]);

    const saveToDB = async (body: IItinerary) => {
        setSubmitting(true);
        body.startTime = (((body.day ?? 0) - 1) * 24 * 60) + ((body.hour ?? 0) * 60) + (body.minute ?? 0);
        const response = await Fetch(`itinerary/${props.singleData.id}`, "PUT", body, null, props.toast);
        if (response) {
            onHide();
            props.toast.current.show({ severity: 'success', detail: "Updated Successfully", life: 3000 });
            props.reloadData();
        }
        setSubmitting(false);
    };

    const loadActivities = async (cityId: number) => {
        const response = await Fetch(`activity/dropdown/${cityId}`, "GET", null, null, null);
        if (response) setActivities(await response.json());
    };

    useEffect(() => {
        if (props.cityId) loadActivities(props.cityId);
    }, [props.cityId]);

    const onHide = () => {
        props.hideDialog();
        reset({});
    };

    const [dayOptions, setDayOptions] = useState<IDropdown[]>([]);
    useEffect(() => {
        if (props.packageDuration) {
            const days = props.packageDuration / 24;
            setDayOptions(Array.from({ length: days + 1 }, (_, i) => ({ id: i + 1, name: `${i + 1}` })));
        }
    }, [props.packageDuration]);

    const hourOptions = Array.from({ length: 24 }, (_, i) => ({ id: i, name: `${i.toString().padStart(2, "0")}` }));
    const minuteOptions = Array.from({ length: 60 }, (_, i) => ({ id: i, name: `${i.toString().padStart(2, "0")}` }));

    return (
        <Dialog visible={props.open} style={{ width: '80vw', maxWidth: '400px' }} header="Edit Itinerary Activity" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(saveToDB)} className="p-fluid formgrid grid">
                <div className="field col-12">{props.destination} | {props.packageTitle} | {props.singleData.checkIn}</div>
                <JVDropdown column={1} required label="Activity" control={control} options={activities} optionLabel="name" optionValue="id" name="activityId" placeholder="Select Activity" error={errors.activityId?.message} />
                <JVInput type="number" required column={1} label="Activity Duration (In Minutes)" control={control} name="duration" error={errors.duration?.message} />
                <div className="field col-12 md:col-3">Start Time<span style={{ color: 'red' }}>*</span></div>
                <JVDropdown column={4} label="Day" control={control} options={dayOptions} optionLabel="name" optionValue="id" name="day" placeholder="Day" error={errors.day?.message} />
                <JVDropdown column={4} label="Hour" control={control} options={hourOptions} optionLabel="name" optionValue="id" name="hour" placeholder="Hour" error={errors.hour?.message} />
                <JVDropdown column={4} label="Minute" control={control} options={minuteOptions} optionLabel="name" optionValue="id" name="minute" placeholder="Minute" error={errors.minute?.message} />
                {endTime && <div className="field col-12 text-blue-400"><small>End Time: {endTime}</small></div>}
                <JVTextArea column={1} label="Description" control={control} name="desc" error={errors.desc?.message} />
                <JVCheckbox column={1} label="Status" control={control} name="isActive" />
                <div className="col-12 flex gap-4 justify-content-end mt-2">
                    <Button style={{ width: '100px' }} loading={submitting} label="UPDATE" type="submit" className="p-button-rounded" />
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200" />
                </div>
            </form>
        </Dialog>
    );
};